import { useEffect, useInsertionEffect, useMemo, useRef } from 'react';
/**
 * Debounces `callback` by `delayMs`. The returned function keeps its identity for a given `delayMs`,
 * always calls the latest `callback`, and drops any pending call on unmount.
 */
export function useDebouncedCallback(callback, delayMs) {
    const callbackRef = useRef(callback);
    useInsertionEffect(() => {
        callbackRef.current = callback;
    });
    const debounced = useMemo(() => {
        let timeoutId;
        const cancel = () => {
            clearTimeout(timeoutId);
            timeoutId = undefined;
        };
        return Object.assign((...args) => {
            cancel();
            timeoutId = setTimeout(() => {
                timeoutId = undefined;
                callbackRef.current(...args);
            }, delayMs);
        }, { cancel });
    }, [delayMs]);
    useEffect(() => debounced.cancel, [debounced]);
    return debounced;
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoidXNlLWRlYm91bmNlZC1jYWxsYmFjay5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uL3NyYy9kZWJvdW5jZS91c2UtZGVib3VuY2VkLWNhbGxiYWNrLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBLE9BQU8sRUFBRSxTQUFTLEVBQUUsa0JBQWtCLEVBQUUsT0FBTyxFQUFFLE1BQU0sRUFBRSxNQUFNLE9BQU8sQ0FBQztBQUl2RTs7O0dBR0c7QUFDSCxNQUFNLFVBQVUsb0JBQW9CLENBQ2xDLFFBQW9DLEVBQ3BDLE9BQWU7SUFFZixNQUFNLFdBQVcsR0FBRyxNQUFNLENBQUMsUUFBUSxDQUFDLENBQUM7SUFFckMsa0JBQWtCLENBQUMsR0FBRyxFQUFFO1FBQ3RCLFdBQVcsQ0FBQyxPQUFPLEdBQUcsUUFBUSxDQUFDO0lBQ2pDLENBQUMsQ0FBQyxDQUFDO0lBRUgsTUFBTSxTQUFTLEdBQUcsT0FBTyxDQUFDLEdBQUcsRUFBRTtRQUM3QixJQUFJLFNBQW9ELENBQUM7UUFFekQsTUFBTSxNQUFNLEdBQUcsR0FBRyxFQUFFO1lBQ2xCLFlBQVksQ0FBQyxTQUFTLENBQUMsQ0FBQztZQUN4QixTQUFTLEdBQUcsU0FBUyxDQUFDO1FBQ3hCLENBQUMsQ0FBQztRQUVGLE9BQU8sTUFBTSxDQUFDLE1BQU0sQ0FDbEIsQ0FBQyxHQUFHLElBQVUsRUFBRSxFQUFFO1lBQ2hCLE1BQU0sRUFBRSxDQUFDO1lBQ1QsU0FBUyxHQUFHLFVBQVUsQ0FBQyxHQUFHLEVBQUU7Z0JBQzFCLFNBQVMsR0FBRyxTQUFTLENBQUM7Z0JBQ3RCLFdBQVcsQ0FBQyxPQUFPLENBQUMsR0FBRyxJQUFJLENBQUMsQ0FBQztZQUMvQixDQUFDLEVBQUUsT0FBTyxDQUFDLENBQUM7UUFDZCxDQUFDLEVBQ0QsRUFBRSxNQUFNLEVBQUUsQ0FDWCxDQUFDO0lBQ0osQ0FBQyxFQUFFLENBQUMsT0FBTyxDQUFDLENBQUMsQ0FBQztJQUVkLFNBQVMsQ0FBQyxHQUFHLEVBQUUsQ0FBQyxTQUFTLENBQUMsTUFBTSxFQUFFLENBQUMsU0FBUyxDQUFDLENBQUMsQ0FBQztJQUUvQyxPQUFPLFNBQVMsQ0FBQztBQUNuQixDQUFDIn0=